import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import * as api from './api.ts';
import type { Membership, Site } from './api.ts';
import { MapSubstrate } from './map-layer.tsx';
import { Banner, Button, InlineAction } from './ui.tsx';
import {
  ActivityIcon,
  BuildingIcon,
  ChipIcon,
  HomeIcon,
  SettingsIcon,
  ShieldIcon,
  TeamIcon,
  UsersIcon,
} from './icons.tsx';

/**
 * The chrome around every screen except the live view.
 *
 * A **fixed 240px rail**, not a collapsible drawer. A drawer saves space nobody
 * needed and costs a click on every navigation, and during an incident it hides
 * where things are. Below 1024px it is replaced by a horizontal strip of the
 * same labels — never icons, which fail under stress and fail WCAG, and never
 * a hover-dependent affordance, because hover has no keyboard and no touch.
 *
 * docs/design-dashboard.md §3.1.
 */

export type Screen =
  | 'home'
  | 'people'
  | 'teams'
  | 'locations'
  | 'hardware'
  | 'settings'
  // Reachable, but not on the rail: readiness (the full report, reached from
  // Home's readiness pill and a Settings link), the history record and the
  // evidence workspace (both under Settings), plus setup and the raise flow
  // (chrome).
  | 'readiness'
  | 'setup'
  // The full-screen building setup wizard, reached at /locations/new from the
  // Locations list's "Add building" CTA and from the empty-org first-run gate.
  // Off the rail (it is a task, not a destination), like setup and the raise
  // flow.
  | 'building-new'
  | 'activity'
  | 'evidence'
  | 'cases'
  | 'disclosure'
  | 'redaction'
  | 'capture'
  | 'activate';

/**
 * Grouped rather than a flat list of ten.
 *
 * Apple's sidebar guidance: *group hierarchy with disclosure controls if
 * your app has a lot of content* — ten equal items is a list you read
 * every time instead of a shape you learn once. The grouping is the
 * product's own split, the one guardrail A5 already draws: readiness is
 * the calm side, 99% of the time; incident is the other 1%.
 *
 * Overview and Settings sit outside both, because they are the way in and
 * the way out rather than part of either job.
 */
/**
 * Five, not ten.
 *
 * The old list named implementation rather than jobs — "Control organisation"
 * and "Presence" and "Venue" are all things the *product* has, and none of them
 * is a thing an admin sets out to do. Grouping ten under two headings made the
 * list scannable without making it shorter, which was treating the symptom.
 *
 * What survived is the questions somebody actually opens this for: what is
 * happening, which buildings, which people, what has happened, and how it is
 * set up. Everything else is a section inside one of those.
 *
 * `activate` is deliberately absent. Raising an alert is not a place you go
 * (`design-dashboard.md` §8) — it is reachable from every screen, so it is a
 * control in the chrome rather than an entry in a list you have to find first.
 */
/**
 * Grouped, not listed — the §7c correction.
 *
 * The five jobs stayed five until the evidence workspace was bolted on as five
 * more flat siblings, re-inflating the rail to the ten-item shape §8a exists to
 * reject. But evidence is not five destinations — it is one workspace with
 * depth (§7c: "group the sidebar once a job earns it"). So the top level is the
 * jobs somebody opens the product for, and Evidence is a labelled section
 * beneath them: Evidence, then the surfaces layered on the same seam (cases,
 * disclosure, redaction) and the way evidence gets in (capture). Settings sits
 * last and ungrouped — the way out, not a job.
 */
/**
 * Four jobs and the way out.
 *
 * The rail had grown back to ten — the four jobs, a Readiness report, an
 * Activity record and a five-item Evidence workspace — which is the bloat the
 * five-screen rebuild (§8a) existed to reject, re-arrived one enterprise
 * surface at a time. The goal is adoption: the café admin who opens this on a
 * Tuesday should see the four things they ever touch and nothing else.
 *
 * So the rail is **Home, People, Devices, Places, Settings**, and everything
 * else moves rather than being deleted (`docs/progressive-disclosure.md`): the
 * Readiness report is reached from Home's readiness pill and a Settings link,
 * the history record and the whole Evidence workspace live under Settings (the
 * workspace
 * gated on the org actually having evidence — DOJ NSW does, a café never sees
 * it), and the enterprise surfaces inside these screens keep disclosing on
 * their existing triggers. Raising an alert stays in the chrome, on every
 * screen, because it is not a place you go.
 */
/**
 * The rail, as a function of one fact: whether this org has the evidence
 * module. Evidence is the only entitlement-gated top-level item — a café never
 * sees it, DOJ NSW does — and it is gated on the entitlement rather than a
 * disclosure surface, because disclosure's Phase-1 evaluators answer
 * true-for-all and would leak the workspace to every org. Everything else is
 * fixed. A secondary link into the workspace also lives under Settings.
 */
function navGroups(
  evidenceEnabled: boolean,
): { heading?: string; items: { id: Screen; label: string; icon: ReactNode }[] }[] {
  // Each rail item carries a leading line-icon (icons.tsx) beside its label —
  // never instead of it (docs/design-dashboard.md §7.3 bans icon-only controls).
  // The glyphs paint in currentColor, so they theme with the rail's ink.
  const jobs: { id: Screen; label: string; icon: ReactNode }[] = [
    // The locked left-rail IA (product owner, 2026-09-07): Home · People ·
    // Teams · Locations · Hardware · Settings (+ Evidence when entitled). Each
    // rail item is a directory of ONE entity → list → detail; any second level
    // lives inside the floating glass panel, never as a stacked rail column.
    //
    // What's happening now — the map, the to-do list, running events.
    { id: 'home', label: 'Home', icon: <HomeIcon size={18} /> },
    // People — a pure directory of people (name, role, team(s), site(s), warden,
    // on-site-if-known) with the person detail drawer. A person's team
    // membership shows and can be quick-assigned here, but the Teams *entity* is
    // managed on the Teams screen, not here.
    { id: 'people', label: 'People', icon: <UsersIcon size={18} /> },
    // Teams — the org-scoped groupings that organise and filter people
    // (docs/teams.md): a list of teams → team detail (members, sites, manager,
    // rename/delete). Its own rail directory because a team is its own entity,
    // not a section of People.
    { id: 'teams', label: 'Teams', icon: <TeamIcon size={18} /> },
    // Locations — the org's BUILDINGS (never "Places", which is reserved for the
    // consumer app): a list of buildings → building detail (zones, assembly
    // points, who sweeps where / the ECO, equipment). Promoted out of Settings
    // to its own rail directory (owner IA, 2026-09-07) — buildings are a first
    // -class entity an admin manages directly, not org-level configuration.
    { id: 'locations', label: 'Locations', icon: <BuildingIcon size={18} /> },
    // What you have — Duress hardware, vehicles and the integrations that
    // bring them in. A connect surface until the ecosystem lands. Named
    // "Hardware", not "Devices", so it cannot be read as the Duress/personal
    // -safety *response* family — this is the physical ecosystem, not the
    // org-directed duress surface.
    //
    // NOTE (duress-response): the org-directed duress surface — the response
    // team reading incoming duress with consented location (migration 0090 /
    // PR #367) — is not built in the dashboard yet (the live surface is
    // polled, a follow-up). It gets its OWN rail item here when that surface
    // ships. It is deliberately NOT given a placeholder now: no screen in this
    // dashboard asserts a state that is not true, and a "coming soon" duress
    // tab would do exactly that.
    { id: 'hardware', label: 'Hardware', icon: <ChipIcon size={18} /> },
  ];
  // The evidence workspace, for the orgs that have it — the DOJ NSW daily
  // job, so it earns a rail item rather than being buried under Settings.
  if (evidenceEnabled)
    jobs.push({ id: 'evidence', label: 'Evidence', icon: <ShieldIcon size={18} /> });
  // Activity — the filterable log of everything that has happened (drills,
  // alerts, escalations, stand-downs and the roster/config changes the audit
  // trail records) and the place the signed reports are generated. Promoted to
  // its own rail item (product owner, 2026-09-07), immediately before Settings:
  // it is a record you review, not org configuration.
  jobs.push({ id: 'activity', label: 'Activity', icon: <ActivityIcon size={18} /> });
  return [
    { items: jobs },
    { items: [{ id: 'settings', label: 'Settings', icon: <SettingsIcon size={18} /> }] },
  ];
}

type Theme = 'light' | 'dark' | 'system';

/**
 * Reading storage can *throw*, not just come back empty — a private window, a
 * browser set to block site data, an embedded webview. Unguarded, that took
 * the whole dashboard down before it rendered a pixel, which is a bad way for
 * a preference about screen brightness to fail.
 *
 * So both directions are wrapped and the fallback is the honest one: follow
 * the operating system.
 */
function readTheme(): Theme {
  try {
    return (localStorage.getItem('evac-theme') as Theme | null) ?? 'system';
  } catch {
    return 'system';
  }
}

function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    try {
      localStorage.setItem('evac-theme', theme);
    } catch {
      // The choice still applies to this session; it just will not be
      // remembered. Losing the preference beats losing the screen.
    }
    const root = document.documentElement;
    // `system` removes the attribute entirely so the media query in styles.css
    // takes over. Setting it to a value would mean the OS preference silently
    // stopped being honoured.
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  return [theme, setTheme];
}

/**
 * The theme control, as one small icon in the top-right of the console chrome —
 * out of the rail's old "Appearance" block (owner review 2026-09-07) and above
 * the notifications strip, where a person reaches for a screen-brightness
 * control.
 *
 * A single button that **cycles light → dark → system** on click. A three-way
 * cycle rather than a long-press for "system": a long-press has no keyboard
 * equivalent, and WCAG AA does not let the third state be reachable by pointer
 * alone. So every state is one tab-stop and one press away, the glyph names the
 * current state (sun / moon / auto), and the accessible label says both where it
 * is and what pressing does. Icons paint in `currentColor`, so it is theme-aware
 * by construction.
 */
const THEME_NEXT: Record<Theme, Theme> = { light: 'dark', dark: 'system', system: 'light' };
const THEME_WORD: Record<Theme, string> = { light: 'Light', dark: 'Dark', system: 'System' };

function ThemeToggle({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }) {
  const next = THEME_NEXT[theme];
  return (
    <button
      type="button"
      onClick={() => onChange(next)}
      aria-label={`Appearance: ${THEME_WORD[theme]}. Switch to ${THEME_WORD[next].toLowerCase()}.`}
      title={`Appearance: ${THEME_WORD[theme]} — switch to ${THEME_WORD[next].toLowerCase()}`}
      className="glass pointer-events-auto fixed top-2 right-3 z-40 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--glass-edge)] text-[var(--ink)] hover:bg-[var(--paper-3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
    >
      <ThemeGlyph theme={theme} />
    </button>
  );
}

/** The per-state glyph, drawn in currentColor so it follows the theme. */
function ThemeGlyph({ theme }: { theme: Theme }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (theme === 'light') {
    // Sun.
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    );
  }
  if (theme === 'dark') {
    // Moon.
    return (
      <svg {...common}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    );
  }
  // System — a monitor, the "follow the OS" state.
  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

/* -------------------------------------------------- banner-region reservation
 *
 * Whether an org shows the "cannot declare a real emergency" banner is only
 * known once the readiness query resolves — a network round trip *after* first
 * paint. Left alone, the banner then appears late and pushes the content sheet
 * down: the load-jump the product owner reported (the People sheet jumping).
 *
 * The fix mirrors the map's own Suspense fallback (`map.tsx`, which reserves the
 * panel height "because a panel that grows when it loads shifts everything under
 * it"): reserve the banner's space up front so it slots in without moving the
 * sheet. To avoid a permanent empty band on the orgs that never show it — a
 * fully-qualified org like DOJ NSW — the reservation is remembered per org in
 * localStorage, exactly like the theme and the map view. A returning operator
 * gets the right reservation from the first paint, so the sheet does not shift
 * in either direction. When nothing is remembered the safe default is to
 * reserve, so a not-yet-qualified org never jumps; the only cost is a single
 * one-time settle on the first ever visit to a qualified org, invisible under
 * the rest of the cold load.
 */
const BANNER_RESERVE_PREFIX = 'evac.banner-reserve:';
/** The Banner's rendered height: py-2.5 (20) + one 19px line + 1px border. */
const BANNER_HEIGHT = 40;

function readBannerReserve(orgId: string): boolean | null {
  try {
    const v = localStorage.getItem(`${BANNER_RESERVE_PREFIX}${orgId}`);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}

function writeBannerReserve(orgId: string, reserve: boolean): void {
  try {
    localStorage.setItem(`${BANNER_RESERVE_PREFIX}${orgId}`, reserve ? '1' : '0');
  } catch {
    // A lost hint just falls back to the safe default (reserve) on the next
    // first paint — a mild repeat, not a trap.
  }
}

export function Shell({
  screen,
  onNavigate,
  orgId,
  bleed,
  orgName,
  memberships,
  onSwitchOrg,
  onSignOut,
  evidenceEnabled,
  sites,
  onFocusSite,
  children,
}: {
  screen: Screen;
  /**
   * Navigate to a screen, optionally to a sub-target within it via a URL hash
   * (e.g. `#golive-drillCompleted` for a specific Going-live step). The hash is
   * stripped when the screen is resolved (main.tsx `parse`), so it only steers
   * the destination screen's own initial state.
   */
  onNavigate: (s: Screen, hash?: string) => void;
  /** Absent only for an account that belongs to no organisation yet. */
  orgId?: string;
  /** Home draws to the edges, because the map is the ground rather than a panel. */
  bleed?: boolean;
  orgName: string;
  /** The org's buildings — the persistent map's default content (it centres on
   *  the single building, or the estate roll-up across all of them). */
  sites?: Site[];
  /** Push a real building selection up to the app so a raise opens on it (H2). */
  onFocusSite?: (siteId: string) => void;
  /**
   * Every org this account belongs to. Two or more turns the org line in the
   * rail into a dropdown; one keeps it a plain label. An operator with more
   * than one org used to see only the first one silently (2026-08-30) — a
   * stuck event under any of the others was invisible.
   */
  memberships?: Membership[];
  onSwitchOrg?: (orgId: string) => void;
  onSignOut: () => void;
  /** Whether this org has the evidence module — adds the Evidence rail item. */
  evidenceEnabled?: boolean;
  children: ReactNode;
}) {
  const [theme, setTheme] = useTheme();

  const groups = navGroups(evidenceEnabled ?? false);
  const nav = groups.flatMap((g) => g.items);

  const readiness = useQuery({
    queryKey: ['live-readiness', orgId],
    queryFn: () => api.getLiveReadiness(orgId!),
    enabled: Boolean(orgId),
    // Not urgent. This answers a question about a calm afternoon, and polling
    // it would put a request on every screen for a state that changes monthly.
    staleTime: 60_000,
  });

  const r = readiness.data;
  // The going-live conditions, in the order Settings' checklist renders them —
  // one source for both the outstanding *count* and the *next* step the banner
  // deep-links to, so the two can never disagree.
  const goLiveSteps = r
    ? ([
        ['allClearTemplates', r.conditions.allClearTemplates.ok],
        // The correction template gate too (migration 0064): a missing one
        // blocks the tier-2 preview-and-retract path outright, and folding
        // it under a bare "templates" number would hide it from the admin
        // shopping around single-admin tier 2.
        ['correctionTemplates', r.conditions.correctionTemplates.ok],
        ['drillCompleted', r.conditions.drillCompleted.ok],
        ['twoAdmins', r.conditions.twoAdmins.ok],
        ['authorityAttested', r.conditions.authorityAttested.ok],
      ] as const)
    : [];
  const outstanding = goLiveSteps.filter(([, ok]) => !ok).length;
  // The banner's "Finish setup" lands on the Going-live checklist and, when a
  // step is outstanding, scrolls straight to the *next* one — not the generic
  // Settings root (owner fix 2026-09-07). The hash steers Settings' initial
  // category (`golive`) and the step it focuses; the screen resolves fine
  // without it, so a fully-qualified org (no outstanding step) simply lands on
  // the checklist.
  const nextStep = goLiveSteps.find(([, ok]) => !ok)?.[0];
  const finishSetupHash = `#golive${nextStep ? `-${nextStep}` : ''}`;

  // Reserve the banner's space from first paint so it does not shove the content
  // sheet down when readiness resolves (see the helpers above). Seeded from the
  // per-org memory; defaults to reserving when unknown so a not-qualified org
  // never jumps.
  const [reserveBanner, setReserveBanner] = useState<boolean>(() =>
    orgId ? (readBannerReserve(orgId) ?? true) : false,
  );
  // Re-seed on an org switch (the shell is not remounted between orgs), and
  // reserve nothing for an account that belongs to no org yet.
  useEffect(() => {
    setReserveBanner(orgId ? (readBannerReserve(orgId) ?? true) : false);
  }, [orgId]);
  // Once readiness resolves, remember the truth: a live-qualified org needs no
  // banner (and no reservation), everything else does. Written per org so the
  // next visit paints the correct reservation with no shift.
  useEffect(() => {
    if (!orgId || !r) return;
    const needed = !r.live;
    setReserveBanner(needed);
    writeBannerReserve(orgId, needed);
  }, [orgId, r]);

  const showBanner = Boolean(r && !r.live);
  // If readiness itself failed to load (not loading, no data), the "cannot
  // declare a real emergency" state is unknown — and silently dropping the band
  // would hide it on the exact failure where it matters most. So keep the
  // reserved band and show a neutral, retryable message instead. Never show the
  // green/all-good state on an error, and never collapse the reserved space.
  const readinessError = Boolean(orgId) && readiness.isError && !r;

  return (
    <MapSubstrate orgId={orgId ?? ''} sites={sites ?? []} onFocusSite={onFocusSite}>
      {/* Everything below floats over the one persistent map: the shell is a
          stacking context at z-10 so the rail, the navs and the content sit
          above the fixed z-0 cartography, and glass panels refract it.

          On Home (`bleed`) this whole z-10 column is `pointer-events-none` so a
          pan/zoom/pin-tap/click-to-place over the *bare map area* falls through
          every ancestor to the z-0 substrate underneath (A, owner correction
          2026-09-07). Before this fix, only `<main>` was none while this
          full-viewport (`min-h-screen`) wrapper and the row below it stayed
          `pointer-events-auto`, so a transparent layer still covered the map and
          swallowed the gesture. The real interactive islands — the rail, the
          top bar/banner, the theme toggle, Home's own sheets, and any open
          inspect overlay — each opt back in with `pointer-events-auto`. When an
          overlay is open, its own `pointer-events-auto` root + frost backdrop
          capture input regardless, and the map is `inert` (mapInteractive is
          gated on `!overlayOpen`), so the map does not pan behind it. Every
          non-bleed screen keeps normal pointer events. */}
      <div
        className={`relative z-10 flex min-h-screen flex-col text-[var(--ink)] ${
          bleed ? 'pointer-events-none' : ''
        }`}
      >
        <a className="skip pointer-events-auto" href="#main">
          Skip to content
        </a>

        {/* The frozen signature: the 1px first-light horizon rule along the top
          of the chrome — blue passing through a breath of gold. The one place
          gold lives on this surface (design-status.md, 2026-08-31). */}
        <div className="horizon-rule" aria-hidden="true" />

        {/* The appearance control: a small icon top-right, above the
            notifications strip, out of the rail's old Appearance block. */}
        <ThemeToggle theme={theme} onChange={setTheme} />

        {/*
        An organisation that cannot declare a real emergency is the one fact
        that must not sit on a settings page (`design-dashboard.md` §7c). It is
        deliberately *not* shown while `live` is true and the scope is narrow —
        that org can alert the people in the building, and a permanent banner
        over a working product is how people learn to stop reading banners.

        It also never appears during an incident, and not by a check: the live
        view renders outside this shell entirely.

        The banner sits in a space-reserving region (min-height = its own
        rendered height) so that when readiness resolves late, the banner slots
        in without pushing the content sheet down — the load-jump the owner
        reported. The region is present from first paint whenever the banner is
        reserved or already showing; see the reservation helpers above.
      */}
        {reserveBanner || showBanner || readinessError ? (
          // A pointer-events island ONLY when it actually carries a control: the
          // banner's "Finish setup" / "Retry" remedy must stay clickable even on
          // Home (where the wrapper is `pointer-events-none`). But when the band
          // is merely *reserved* (readiness not yet resolved, no banner and no
          // error), it is an empty 40px spacer over Home's map top — leaving it
          // `pointer-events-auto` swallowed pan/zoom gestures there, so an empty
          // spacer opts out (owner review 2026-09-07).
          <div
            className={showBanner || readinessError ? 'pointer-events-auto' : ''}
            style={{ minHeight: BANNER_HEIGHT }}
          >
            {showBanner ? (
              <Banner
                action={
                  <InlineAction onClick={() => onNavigate('settings', finishSetupHash)}>
                    Finish setup
                  </InlineAction>
                }
              >
                {/*
            The tail is deliberately worded around declaring a real emergency,
            not "N steps remain" — Home's readiness pill carries the "things need
            attention" count, and two nudges that both ended "N steps remain"
            with different numbers and destinations read as the same thing said
            twice. This one is about live qualification; that one is about
            readiness.
          */}
                <span>
                  This organisation can run drills but cannot declare a real emergency
                  {outstanding > 0 ? (
                    <>
                      {' — '}
                      <strong className="font-medium">{outstanding}</strong>
                      {outstanding === 1 ? ' step remains' : ' steps remain'} before it can
                    </>
                  ) : null}
                  .
                </span>
              </Banner>
            ) : readinessError ? (
              /*
              Readiness could not be loaded. We do not know whether this org can
              declare a real emergency, so we neither show the green all-good
              state nor drop the band — we say so, and offer a retry, keeping the
              reserved space so the sheet does not shift.
            */
              <Banner
                action={<InlineAction onClick={() => void readiness.refetch()}>Retry</InlineAction>}
              >
                <span>Couldn’t confirm this organisation’s emergency readiness.</span>
              </Banner>
            ) : null}
          </div>
        ) : null}

        {/* Below 1024px the rail becomes a horizontal strip rather than a
        drawer. Same reasoning: nothing that hides where things are.

        It sits in the column above the content row, not inside it. As a
        sibling of <main> in a flex *row* it was laid out as a narrow left
        column sharing width with the page — a strip in the markup and a
        rail on the screen. */}
        <nav
          aria-label="Sections"
          className="glass pointer-events-auto border-b border-[var(--glass-edge)] lg:hidden"
        >
          <ul className="m-0 flex list-none gap-1 overflow-x-auto p-2">
            {nav.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onNavigate(item.id)}
                  aria-current={item.id === screen ? 'page' : undefined}
                  className={`inline-flex h-10 items-center gap-2 rounded-[var(--radius-1)] px-3 text-[14px] whitespace-nowrap ${
                    item.id === screen ? 'bg-[var(--paper-3)] font-medium' : 'text-[var(--ink-2)]'
                  }`}
                >
                  <span aria-hidden className="shrink-0">
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          {/*
            Below 1024px the desktop rail is gone, and with it the org switcher
            (only ever shown for an account in more than one org) and — the one
            that cannot be missing at any width — Raise an alert. There is no
            longer a global site/location switcher here or in the rail: the
            dashboard shows every location the operator may see and filtering is
            per-screen (People/Teams/Activity carry their own site filter), while
            the map picks a sensible default building on its own. A compact
            control row rather than a second full rail: the org switcher (when it
            applies) to the left, Raise pinned to the right so it is always in the
            same place. `design-dashboard.md` §8: raising is reachable from every
            screen.
          */}
          <div className="flex items-center gap-2 border-t border-[var(--glass-edge)] px-2 py-2">
            {memberships && memberships.length > 1 && onSwitchOrg ? (
              <select
                aria-label="Switch organisation"
                value={orgId ?? ''}
                onChange={(ev) => onSwitchOrg(ev.target.value)}
                className="min-w-0 max-w-[60%] shrink truncate rounded-[var(--radius-1)] border border-[var(--line-2)] bg-transparent px-2 py-1.5 text-[13px] text-[var(--ink)] focus:outline-none"
              >
                {memberships.map((m) => (
                  <option key={m.org_id} value={m.org_id}>
                    {m.org_name}
                  </option>
                ))}
              </select>
            ) : null}

            <div className="ml-auto shrink-0">
              <Button variant="primary" onClick={() => onNavigate('activate')}>
                Raise an alert
              </Button>
            </div>
          </div>
        </nav>

        {/* The content row inherits the wrapper's `pointer-events-none` on Home
            so the bare map area passes gestures through; the rail and <main>'s
            own islands opt back in below. */}
        <div className="flex flex-1">
          {/*
          Fixed, and glass, so the map on Home runs underneath it
          (`design-dashboard.md` §8b). A sidebar that sits *beside* content has
          nothing behind it to refract, and glass over a flat colour is a grey
          panel wearing an expensive name. A pointer-events island, so it stays
          clickable over Home's pass-through content layer.
        */}
          <nav
            aria-label="Sections"
            className="glass pointer-events-auto fixed top-0 left-0 z-20 hidden h-screen w-[240px] shrink-0 overflow-y-auto border-r border-[var(--glass-edge)] px-3 py-5 lg:block"
          >
            <div className="mb-6 px-2">
              <div className="text-[14px] font-[650]">EVAC</div>
              {/*
              A native select when there is more than one org, plain text
              otherwise. Native for the same reason SweepControl uses one: it
              carries the OS's own keyboard and screen-reader behaviour, and
              on a 3am incident that is worth more than a bespoke menu.

              Full ink, not muted: at 65% the rail is see-through enough that
              secondary text would sit at 2.9:1. Transparency is paid for in
              contrast (`design-dashboard.md` §8b).
            */}
              {memberships && memberships.length > 1 && onSwitchOrg ? (
                <select
                  aria-label="Switch organisation"
                  value={orgId ?? ''}
                  onChange={(ev) => onSwitchOrg(ev.target.value)}
                  className="mt-0.5 -mx-1 max-w-full truncate rounded-[var(--radius-1)] bg-transparent px-1 text-[13px] text-[var(--ink)] hover:bg-[var(--paper-3)] focus:bg-[var(--paper-3)] focus:outline-none"
                >
                  {memberships.map((m) => (
                    <option key={m.org_id} value={m.org_id}>
                      {m.org_name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-0.5 truncate text-[13px] text-[var(--ink)]">{orgName}</div>
              )}

              {/*
              There is deliberately no global site/location switcher in the rail
              (owner decision 2026-09-08). The dashboard shows every location the
              operator is permitted to see, and filtering is per-screen — People,
              Teams and Activity each carry a Site filter in their own header
              band, Locations *is* the list of buildings, and the persistent map
              picks a sensible default (the single building, or the estate
              roll-up across all of them) and is re-focused by tapping a pin on
              Home rather than a switcher up here.
            */}
            </div>

            <div className="space-y-5">
              {groups.map((group, gi) => (
                <div key={group.heading ?? gi}>
                  {group.heading ? (
                    <div className="mb-1 px-2 text-[11px] font-[600] tracking-[0.06em] text-[var(--ink-2)] uppercase">
                      {group.heading}
                    </div>
                  ) : null}
                  <ul className="m-0 list-none space-y-0.5 p-0">
                    {group.items.map((item) => {
                      const active = item.id === screen;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => onNavigate(item.id)}
                            aria-current={active ? 'page' : undefined}
                            className={`flex h-9 w-full items-center gap-2.5 rounded-[var(--radius-1)] px-2 text-left text-[14px] transition-colors ${
                              active
                                ? 'bg-[var(--paper-3)] font-medium text-[var(--ink)]'
                                : 'text-[var(--ink)] hover:bg-[var(--paper-3)]'
                            }`}
                          >
                            {/* Leading glyph in currentColor — it inherits the
                                rail's full ink (the rail carries full ink only,
                                §8b) and its active state. aria-hidden: the label
                                is the name. */}
                            <span aria-hidden className="shrink-0">
                              {item.icon}
                            </span>
                            {item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {/*
            Raising an alert is not a place you go.
 
            It sat in a list of ten as "Activate", which meant an operator with
            something happening had to find the right word in a menu first. It
            is a control in the chrome now, present on every screen — and it
            *opens* the flow rather than sending anything, because a
            permanently-visible control that sent an alert on one press is a
            different and much worse product.
          */}
            <div className="mt-6 px-1">
              <Button variant="primary" full onClick={() => onNavigate('activate')}>
                Raise an alert
              </Button>
            </div>

            {/* The theme toggle used to live here as an "Appearance" block; it
                is now a small icon in the top-right chrome (owner review
                2026-09-07). Sign out stays as the way out of the rail. */}
            <div className="mt-8 border-t border-[var(--line)] pt-4">
              <button
                type="button"
                onClick={onSignOut}
                className="h-10 w-full rounded-[var(--radius-1)] px-2 text-left text-[14px] text-[var(--ink)]"
              >
                Sign out
              </button>
            </div>
          </nav>

          {/*
          `bleed` is Home: it manages its own edges because the map has to
          reach them. Every other screen keeps the measure — a table read at a
          desk does not want to be 2000px wide.
        */}
          {/*
            On Home (`bleed`) the content layer must let map gestures through to
            the z-0 substrate underneath — otherwise this transparent, full
            -viewport <main> at z-10 intercepts every pan, zoom, pin-tap and
            click-to-place before it can reach the map. So Home's <main> is
            `pointer-events-none`, and the overlay islands that must stay live
            (the notifications strip, the pin card, the place affordance) opt
            back in with `pointer-events-auto`. Every other screen keeps normal
            pointer events: its content sheet is what the operator interacts with
            and the map beneath is deliberately ambient.
          */}
          <main
            id="main"
            className={`relative z-10 min-w-0 flex-1 lg:ml-[240px] ${
              bleed ? 'pointer-events-none' : 'flex flex-col'
            }`}
          >
            {/*
            Home (`bleed`) manages its own edges: its map is the shell's, and it
            floats its own glass overlays over it. Every other screen is one
            continuous glass sheet over that same map — edge to edge, not a card
            floating in a centred column (Trav, 2026-09-07). It fills `<main>`
            to its edges: right and bottom to the viewport, left to the rail
            offset `<main>` already carries (`lg:ml-[240px]`), top just under the
            horizon rule and the banner reserve. It keeps the console's `.sheet`
            material — tuned so `--ink`/`--ink-2` stay WCAG-AA over moving
            cartography (`design-dashboard.md` §8b), so the map still refracts
            through it — but drops the card's radius and floating elevation and
            reaches to the edges (`flex-1` fills main's height, so no map shows
            through above, below or around it). No screen paints an opaque ground
            over the map any more.
          */}
            {bleed ? (
              children
            ) : (
              // Inner padding is `--sheet-pad` (styles.css), not a literal, so
              // the Directory header band can cancel it with matching negative
              // margins and bleed its fill to the sheet edges. Keep them on the
              // one var — never re-hardcode `p-5 sm:p-7` here. (A `bleed` screen
              // like Home takes the branch above and must never host a Directory,
              // whose negative margins would then overhang the unpadded main.)
              <div className="sheet flex-1 border-t border-[var(--glass-edge)] p-[var(--sheet-pad)]">
                {children}
              </div>
            )}
          </main>
        </div>
      </div>
    </MapSubstrate>
  );
}
